import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { toast } from 'sonner';
import { format as fmtDate, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { TIME_SLOTS, getEndSlots } from '../utils/timeSlots';
import { BookableFormModal } from '../components/BookableFormModal';
import AvailabilityView from '../components/AvailabilityView';
import { ProfileModal } from '../components/ProfileModal';
import { ContractsTab } from '../components/contracts/ContractsTab';
import { FeedbackTab } from '../components/feedback/FeedbackTab';
import { CreditsOverviewTab } from '../components/credits/CreditsOverviewTab';
import { DayPassOverviewTab } from '../components/day_passes/DayPassOverviewTab';
import RoleAnalytics from '../components/analytics/RoleAnalytics';
import { useHolidays } from '../hooks/useQueries';
import {
  WalkInModal, BlockSlotModal, AddUserModal, AddOrgModal,
  AllocateCreditsModal, InternalBookingModal,
  WalletViewerModal, AdjustCreditsModal, DeleteConfirmModal,
  EditOrgModal, EditUserModal, InternalClientModal, LogVisitorModal, SellCreditsModal
} from '../components/cm/CMModals';
import {
  Calendar,
  Clock,
  Users,
  Building2,
  CheckCircle,
  XCircle,
  Plus,
  RefreshCw,
  Loader2,
  Sun,
  Moon,
  LogOut,
  BarChart3,
  TrendingUp,
  IndianRupee,
  Coins,
  UserPlus,
  Briefcase,
  CalendarDays,
  CalendarClock,
  UserCheck,
  UserX,
  Ban,
  Eye,
  Edit,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Search,
  Filter,
  Download,
  FileText,
  Trash2,
  Pencil,
  Copy,
  ExternalLink,
  Link2,
  Share2,
  Star,
  Sparkles,
  ClipboardList,
  Package,
  Ticket,
  Layers,
  Building,
} from 'lucide-react';
import { shareViaWhatsApp, WhatsAppIcon } from '../lib/whatsapp';
import { ApprovalsModule } from '../components/approvals/ApprovalsModule';
import { ApprovalsBell } from '../components/approvals/ApprovalsBell';
import VirtualOfficeTab from '../components/virtual_office/VirtualOfficeTab';
import { EcosystemInquiriesTab } from '../components/ecosystem/EcosystemInquiriesTab';
import { InventoryTab } from '../components/inventory/InventoryTab';
import { AlertsBanner } from '../components/inventory/AlertsBanner';
import { ParkingConfigModal, FloorsConfigModal } from '../components/admin/VenuesTab';
import { VMSTab } from '../components/vms/VMSTab';
import { RescheduleBookingModal } from '../components/bookings/RescheduleBookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Matches _ADDON_LABELS in backend/email_service.py
const ADDON_LABELS = {
  parking: 'Parking',
  sandwich: 'Sandwich',
  cookies: 'Cookies',
  stationery: 'Stationery',
};

const getAddOnChips = (booking) => {
  const addOns = booking?.add_ons;
  if (!addOns) return [];
  return Object.entries(addOns)
    .filter(([, qty]) => qty > 0)
    .map(([key, qty]) => ({ key, qty, label: ADDON_LABELS[key] || key }));
};

const CenterManagerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, api } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { data: holidaysList = [] } = useHolidays();
  const holidayDates = useMemo(() => {
    const map = {};
    holidaysList.forEach((h) => { map[h.date] = h.name; });
    return map;
  }, [holidaysList]);

  // State
  const [loading, setLoading] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeTab, setActiveTab] = useState('today');
  const [inventorySubTabHint, setInventorySubTabHint] = useState(null);
  const [parkingVenue, setParkingVenue] = useState(null);
  const [floorsVenue, setFloorsVenue] = useState(null);
  const [venues, setVenues] = useState([]);
  // All active centres system-wide (not scoped to this CM's assignments) — used for
  // Log a Visitor, since a CM may log a visitor at any centre, not just their own.
  const [allVenues, setAllVenues] = useState([]);
  const [bookables, setBookables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [creditSearchQuery, setCreditSearchQuery] = useState('');
  const [bookingDateFilter, setBookingDateFilter] = useState('all');
  const [bookingCustomStart, setBookingCustomStart] = useState(null);
  const [bookingCustomEnd, setBookingCustomEnd] = useState(null);
  const [showBkStartCal, setShowBkStartCal] = useState(false);
  const [showBkEndCal, setShowBkEndCal] = useState(false);
  const [bookingPage, setBookingPage] = useState(1);
  const BOOKING_PAGE_SIZE = 50;
  
  // Modal states
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [showSellCreditsModal, setShowSellCreditsModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showBookableModal, setShowBookableModal] = useState(false);
  const [editingBookable, setEditingBookable] = useState(null);
  const [bookableFormVenueId, setBookableFormVenueId] = useState(null);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showInternalModal, setShowInternalModal] = useState(false);
  const [showInternalClientModal, setShowInternalClientModal] = useState(false);
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  // Edit booking state
  const [showEditBookingModal, setShowEditBookingModal] = useState(false);
  const [editBookingForm, setEditBookingForm] = useState({});
  
  // Ticket states
  const [cmTickets, setCmTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  
  // Form states
  const [internalForm, setInternalForm] = useState({
    venue_id: '',
    bookable_id: '',
    booked_by_name: '',
    booked_by_email: '',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    notes: ''
  });
  const emptyWalkInSlot = () => ({
    _key: Math.random().toString(36).slice(2),
    venue_id: '',
    bookable_id: '',
    start_time: '',
    end_time: '',
    booking_date: new Date().toISOString().split('T')[0],
    discount_percent: 0,
    parking_qty: 0, parking_2w_qty: 0,
    pass_quantity: 1, pass_days: 1,
  });
  const [walkInForm, setWalkInForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    payment_method: 'razorpay',
    gstin: '', company_name: '', company_address: '',
    apply_sale_offer: false,
    slots: [emptyWalkInSlot()],
  });
  
  const [visitorForm, setVisitorForm] = useState({
    visitor_type: 'client',
    venue_id: '',
    product_enquiry: '',
    visitor_name: '',
    visitor_email: '',
    visitor_phone: '',
    visit_date: new Date().toISOString().split('T')[0],
    seats_required: 1,
    start_time: '',
    host_name: '',
    host_company: '',
    notes: '',
  });

  const [blockForm, setBlockForm] = useState({
    venue_id: '',
    bookable_id: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '18:00',
    reason: ''
  });
  
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'client',
    organization_id: ''
  });
  
  const [orgForm, setOrgForm] = useState({
    name: '',
    gstin: '',
    billing_email: '',
    phone: '',
    contact_person: ''
  });
  
  const [creditForm, setCreditForm] = useState({
    target_type: 'organization',
    target_id: '',
    monthly_credits: 0,
    monthly_valid_till: '',
    carry_forward_percentage: 0,
    renewal_cycle_months: 1,
    additional_credits: 0,
    additional_valid_till: ''
  });
  
  const [bookableForm, setBookableForm] = useState({
    name: '',
    venue_id: '',
    bookable_type: 'meeting_room',
    capacity: 4,
    hourly_rate: 500,
    description: '',
    amenities: {
      wifi: true,
      whiteboard: true,
      tv_screen: true,
      ac: true,
      projector: false,
      video_conferencing: false,
      coffee_tea: false,
      water: true,
      power_outlets: true,
      natural_light: false
    }
  });
  
  const [saving, setSaving] = useState(false);

  // Wallet viewer state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletViewData, setWalletViewData] = useState(null);
  const [walletViewTarget, setWalletViewTarget] = useState('');
  // Adjust credits state
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ org_id: '', user_id: '', current_balance: 0, new_balance: 0, reason: '' });
  const [adjustTarget, setAdjustTarget] = useState('');
  // Delete confirm state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: '', name: '', type: '' });
  // Edit org/user state
  const [showEditOrgModal, setShowEditOrgModal] = useState(false);
  const [editOrgForm, setEditOrgForm] = useState({});
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserForm, setEditUserForm] = useState({});
  // Payment link result state
  const [paymentLinkResult, setPaymentLinkResult] = useState(null);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  // Internal client form state
  const [internalClientForm, setInternalClientForm] = useState({
    organization_id: '', user_id: '', venue_id: '', bookable_id: '',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '', end_time: '', attendees: 1, notes: '', discount_percent: 0
  });
  const [orgCreditInfo, setOrgCreditInfo] = useState(null);
  const [creditRates, setCreditRates] = useState(null);

  // Styling
  const cardBg = isDark ? 'bg-card' : 'bg-white';
  const cardBorder = isDark ? 'border-border' : 'border-gray-200';
  const innerCardBg = isDark ? 'bg-background/50' : 'bg-gray-50';
  const inputBg = isDark ? 'bg-background' : 'bg-white';

  // State for center manager stats and today's schedule
  const [cmStats, setCmStats] = useState(null);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [assignedCentreIds, setAssignedCentreIds] = useState([]);

  // Fetch credit rates once
  useEffect(() => {
    api.get('/credits/rates').then(r => setCreditRates(r.data)).catch(() => {});
  }, [api]);

  // Fetch org credit wallet when org changes in IC form
  useEffect(() => {
    const orgId = internalClientForm.organization_id;
    if (!orgId) { setOrgCreditInfo(null); return; }
    api.get(`/credits/wallet/organization/${orgId}`)
      .then(r => setOrgCreditInfo(r.data))
      .catch(() => setOrgCreditInfo(null));
  }, [internalClientForm.organization_id, api]);

  // Fetch data
  useEffect(() => {
    fetchAllData();
  }, [api]);

  const fetchAllData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      // First get assigned centres
      const myCentresRes = await api.get('/centre-assignments/my-centres');
      const myCentres = myCentresRes.data;
      const myCentreIds = myCentres.map(c => c.id);
      setAssignedCentreIds(myCentreIds);

      const [bookablesRes, bookingsRes, usersRes, orgsRes, analyticsRes, statsRes, scheduleRes, allVenuesRes] = await Promise.all([
        api.get('/bookables/all'),
        api.get('/bookings'),
        api.get('/users'),
        api.get('/organizations'),
        api.get('/analytics/dashboard'),
        api.get('/center-manager/stats').catch(() => ({ data: null })),
        api.get('/center-manager/today-schedule').catch(() => ({ data: [] })),
        api.get('/venues').catch(() => ({ data: [] })),
      ]);

      // Filter to assigned centres only (for CM role, backend scoping handles schedule/stats)
      const allBookables = bookablesRes.data;
      const scopedBookables = myCentreIds.length > 0
        ? allBookables.filter(b => myCentreIds.includes(b.venue_id))
        : allBookables;
      const scopedBookings = myCentreIds.length > 0
        ? bookingsRes.data.filter(b => myCentreIds.includes(b.venue_id))
        : bookingsRes.data;

      setVenues(myCentres);
      setAllVenues(allVenuesRes.data || []);
      setBookables(scopedBookables);
      setBookings(scopedBookings);
      setUsers(usersRes.data);
      setOrganizations(orgsRes.data);
      setAnalytics(analyticsRes.data);
      setCmStats(statsRes.data);
      setTodaySchedule(scheduleRes.data || []);
      
      // Filter today's bookings
      const todayList = scopedBookings.filter(b => b.booking_date === today);
      setTodayBookings(todayList);

      // Fetch tickets
      try {
        const ticketsRes = await api.get('/tickets');
        setCmTickets(ticketsRes.data);
      } catch (e) { /* non-critical */ }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check-in/Check-out handlers
  const handleCheckIn = async (bookingId) => {
    try {
      await api.post(`/center-manager/check-in/${bookingId}`);
      toast.success('Guest checked in successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in guest');
    }
  };

  const handleCheckOut = async (bookingId) => {
    try {
      await api.post(`/center-manager/check-out/${bookingId}`);
      toast.success('Guest checked out successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out guest');
    }
  };

  // Walk-in booking handler — supports several independent slots (own
  // venue/space/date/time each) sharing one combined payment link.
  const handleWalkInBooking = async () => {
    if (!walkInForm.customer_name) {
      toast.error('Please fill in required fields');
      return;
    }
    for (const s of walkInForm.slots) {
      if (!s.venue_id || !s.bookable_id || !s.booking_date) {
        toast.error('Please complete venue, space and date for every slot');
        return;
      }
    }
    // GSTIN flow: when filled, company name & address must also be present
    const gstinTrim = (walkInForm.gstin || '').trim();
    if (gstinTrim) {
      if (gstinTrim.length !== 15) { toast.error('GSTIN must be 15 characters'); return; }
      if (!walkInForm.company_name?.trim() || !walkInForm.company_address?.trim()) {
        toast.error('Company name and address are required when GSTIN is provided');
        return;
      }
    }
    if (walkInForm.payment_method === 'razorpay' && !walkInForm.customer_email) {
      toast.error('Customer email is required to send a Razorpay payment link. Choose "Manual / Cash" if the client has no email.');
      return;
    }
    if (walkInForm.apply_sale_offer && !walkInForm.customer_email) {
      toast.error("Customer email is required to apply the sale offer. The rewards go to their Clayworks account.");
      return;
    }

    setSaving(true);
    try {
      // Always use the guest endpoint — never auto-create a user or link the
      // booking to the CM's own account. Walk-in/guest bookings must reflect
      // the customer's name & details, not the CM's.
      const body = {
        guest_name: walkInForm.customer_name,
        guest_email: walkInForm.customer_email || '',
        guest_phone: walkInForm.customer_phone || '',
        gstin: walkInForm.gstin || '',
        company_name: walkInForm.company_name || '',
        company_address: walkInForm.company_address || '',
        payment_method: walkInForm.payment_method === 'manual' ? 'cash' : 'razorpay',
        apply_sale_offer: !!walkInForm.apply_sale_offer,
        slots: walkInForm.slots.map(s => ({
          venue_id: s.venue_id,
          bookable_id: s.bookable_id,
          booking_date: s.booking_date,
          start_time: s.start_time,
          end_time: s.end_time,
          discount_percent: parseFloat(s.discount_percent) || 0,
          parking_qty: parseInt(s.parking_qty) || 0,
          parking_2w_qty: parseInt(s.parking_2w_qty) || 0,
          parking_basis: 'day',
          parking_4w_rate: parseFloat(s.parking_4w_rate) || 0,
          parking_2w_rate: parseFloat(s.parking_2w_rate) || 0,
          pass_quantity: parseInt(s.pass_quantity) || 1,
          pass_days: parseInt(s.pass_days) || 1,
        })),
      };
      const res = await api.post('/bookings/guest-multi', body);
      const offer = res.data.sale_offer;
      const accountNote = offer?.account_created ? ` New account created for ${offer.customer_email}; set-password email sent.` : '';
      if (walkInForm.payment_method === 'razorpay') {
        setPaymentLinkResult({
          payment_link: res.data.payment_link,
          total_amount: res.data.total_amount,
          slots: res.data.slots,
          guest_email: walkInForm.customer_email,
          guest_name: walkInForm.customer_name,
          guest_phone: walkInForm.customer_phone,
          sale_offer: offer,
          message: (res.data.message || 'Payment link sent to customer') + accountNote,
        });
      } else {
        const earned = offer?.rewards?.length ? ` Offer applied: ${offer.rewards.map(r => r.summary).join(', ')} added to ${offer.customer_email}.` : '';
        toast.success(`${res.data.slot_count || 1} walk-in booking(s) confirmed for ${walkInForm.customer_name} (paid via cash).${earned}${accountNote}`, { duration: 8000 });
      }

      setShowWalkInModal(false);
      setWalkInForm({
        customer_name: '', customer_email: '', customer_phone: '',
        payment_method: 'razorpay',
        gstin: '', company_name: '', company_address: '',
        apply_sale_offer: false,
        slots: [emptyWalkInSlot()],
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create walk-in booking');
    } finally {
      setSaving(false);
    }
  };

  // Log Visitor handler
  const handleLogVisitor = async () => {
    const isVendor = visitorForm.visitor_type === 'vendor';
    const isGuest = visitorForm.visitor_type === 'guest';
    if (!visitorForm.venue_id || !visitorForm.visitor_name) {
      toast.error('Please fill in required fields');
      return;
    }
    if (isVendor || isGuest) {
      if (!visitorForm.visitor_phone || !visitorForm.start_time) {
        toast.error('Please fill in required fields');
        return;
      }
      if (isGuest && !visitorForm.host_name?.trim()) {
        toast.error('Please fill in required fields');
        return;
      }
    } else if (!visitorForm.product_enquiry || !visitorForm.visitor_email || !visitorForm.visitor_phone || !visitorForm.visit_date || !visitorForm.seats_required) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await api.post('/visitor-logs', {
        visitor_type: visitorForm.visitor_type,
        venue_id: visitorForm.venue_id,
        product_enquiry: (isVendor || isGuest) ? null : visitorForm.product_enquiry,
        visitor_name: visitorForm.visitor_name,
        visitor_email: visitorForm.visitor_email || '',
        visitor_phone: visitorForm.visitor_phone || '',
        visit_date: visitorForm.visit_date || new Date().toISOString().split('T')[0],
        seats_required: (isVendor || isGuest) ? null : (parseInt(visitorForm.seats_required) || 1),
        start_time: (isVendor || isGuest) ? visitorForm.start_time : null,
        end_time: null,
        host_name: isGuest ? (visitorForm.host_name || '') : null,
        host_company: isGuest ? (visitorForm.host_company || '') : null,
        notes: visitorForm.notes || '',
      });
      toast.success(`Visitor ${visitorForm.visitor_name} logged successfully`);
      setShowVisitorModal(false);
      setVisitorForm({
        visitor_type: 'client', venue_id: '', product_enquiry: '', visitor_name: '', visitor_email: '', visitor_phone: '',
        visit_date: new Date().toISOString().split('T')[0], seats_required: 1, start_time: '', host_name: '', host_company: '', notes: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log visitor');
    } finally {
      setSaving(false);
    }
  };

  // Internal booking handler
  const handleInternalBooking = async () => {
    if (!internalForm.venue_id || !internalForm.bookable_id || !internalForm.booked_by_name || !internalForm.booking_date || !internalForm.start_time || !internalForm.end_time) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.post('/bookings', {
        venue_id: internalForm.venue_id,
        bookable_id: internalForm.bookable_id,
        booking_date: internalForm.booking_date,
        start_time: internalForm.start_time,
        end_time: internalForm.end_time,
        attendees: 1,
        notes: `Internal: ${internalForm.booked_by_name}${internalForm.booked_by_email ? ' (' + internalForm.booked_by_email + ')' : ''}\n${internalForm.notes || ''}`.trim(),
        is_internal: true,
        booked_for: internalForm.booked_by_name,
        use_credits: false
      });
      toast.success('Internal booking created');
      setShowInternalModal(false);
      setInternalForm({
        venue_id: '', bookable_id: '', booked_by_name: '', booked_by_email: '',
        booking_date: new Date().toISOString().split('T')[0], start_time: '', end_time: '', notes: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create internal booking');
    } finally {
      setSaving(false);
    }
  };

  // Internal client booking handler (book for org member)
  const handleInternalClientBooking = async () => {
    if (!internalClientForm.organization_id || !internalClientForm.venue_id || !internalClientForm.bookable_id || !internalClientForm.booking_date || !internalClientForm.start_time || !internalClientForm.end_time) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/bookings/internal-client', {
        ...internalClientForm,
        discount_percent: parseFloat(internalClientForm.discount_percent) || 0,
      });

      setShowInternalClientModal(false);

      if (res.data.paid_with_credits) {
        toast.success(`Booked with ${res.data.credits_used.toFixed(1)} credits (rate: ${res.data.credit_rate}/seat/hr). Remaining: ${res.data.wallet_balance.toFixed(1)}`);
      } else if (res.data.payment_link) {
        const selectedUser = users.find(u => u.id === internalClientForm.user_id);
        const selectedOrg = organizations.find(o => o.id === internalClientForm.organization_id);
        const skipReason = res.data.credit_skip_reason;
        if (skipReason) {
          toast.warning(`Credits not used: ${skipReason.replace(/_/g, ' ')}. Payment link generated.`);
        }
        setPaymentLinkResult({
          payment_link: res.data.payment_link,
          total_amount: res.data.total_amount,
          guest_email: selectedUser?.email || '',
          guest_name: selectedUser?.name || selectedOrg?.name || 'Client',
          guest_phone: selectedUser?.phone || '',
          message: res.data.message
        });
      } else {
        const skipReason = res.data.credit_skip_reason;
        if (skipReason) {
          toast.warning(`Credits not used: ${skipReason.replace(/_/g, ' ')}. ${res.data.message}`);
        } else {
          toast.success(res.data.message);
        }
      }

      setInternalClientForm({
        organization_id: '', user_id: '', venue_id: '', bookable_id: '',
        booking_date: new Date().toISOString().split('T')[0],
        start_time: '', end_time: '', attendees: 1, notes: '', discount_percent: 0
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking for client');
    } finally {
      setSaving(false);
    }
  };


  // Block time slot handler
  const handleBlockTimeSlot = async () => {
    if (!blockForm.venue_id || !blockForm.bookable_id || !blockForm.reason) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    try {
      // Use the center manager block slot endpoint
      await api.post('/center-manager/block-slot', {
        venue_id: blockForm.venue_id,
        bookable_id: blockForm.bookable_id,
        booking_date: blockForm.date,
        start_time: blockForm.start_time,
        end_time: blockForm.end_time,
        reason: blockForm.reason
      });
      
      toast.success('Time slot blocked successfully');
      setShowBlockModal(false);
      setBlockForm({
        venue_id: '',
        bookable_id: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '18:00',
        reason: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block time slot');
    } finally {
      setSaving(false);
    }
  };

  // Add user handler
  const handleAddUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/users/create', {
        ...userForm,
        user_type: userForm.organization_id ? 'b2b' : 'b2c'
      });
      
      toast.success('User created successfully');
      setShowUserModal(false);
      setUserForm({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'client',
        organization_id: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  // Add organization handler
  const handleAddOrg = async () => {
    if (!orgForm.name || !orgForm.billing_email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/organizations', orgForm);
      
      toast.success('Organization created successfully');
      setShowOrgModal(false);
      setOrgForm({
        name: '',
        gstin: '',
        billing_email: '',
        phone: '',
        contact_person: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  // Allocate credits handler
  const handleAllocateCredits = async () => {
    if (!creditForm.target_id) {
      toast.error('Please select a target');
      return;
    }
    const totalCredits = (creditForm.monthly_credits || 0) + (creditForm.additional_credits || 0);
    if (totalCredits <= 0) {
      toast.error('Please enter credits in at least one section');
      return;
    }
    
    setSaving(true);
    try {
      const targetKey = creditForm.target_type === 'organization' ? 'organization_id' : 'user_id';

      // Monthly credits allocation (subject to auto-renew + carry-forward)
      if (creditForm.monthly_credits > 0) {
        const payload = {
          [targetKey]: creditForm.target_id,
          credits: creditForm.monthly_credits,
          credit_type: 'monthly',
          monthly_allocation: creditForm.monthly_credits,
          auto_renew: creditForm.auto_renew !== undefined ? creditForm.auto_renew : true,
          carry_forward_percentage: creditForm.carry_forward_percentage || 0,
          renewal_cycle_months: creditForm.renewal_cycle_months || 1,
          renewal_expiry_date: creditForm.monthly_valid_till || null,
        };
        if (creditForm.monthly_valid_till) {
          payload.valid_until = `${creditForm.monthly_valid_till}-01T00:00:00Z`;
        }
        await api.post('/credits/allocate', payload);
      }

      // Additional one-time credits → TOP-UP bucket (never expires)
      if (creditForm.additional_credits > 0) {
        const payload = {
          [targetKey]: creditForm.target_id,
          credits: creditForm.additional_credits,
          credit_type: 'topup',
        };
        await api.post('/credits/allocate', payload);
      }
      
      toast.success('Credits allocated successfully');
      setShowCreditModal(false);
      setCreditForm({
        target_type: 'organization',
        target_id: '',
        monthly_credits: 0,
        monthly_valid_till: '',
        carry_forward_percentage: 0,
        renewal_cycle_months: 1,
        additional_credits: 0,
        additional_valid_till: ''
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate credits');
    } finally {
      setSaving(false);
    }
  };

  // View wallet for org/user
  const handleViewWallet = async (targetType, targetId, targetName) => {
    setWalletViewTarget(targetName);
    setWalletViewData(null);
    setShowWalletModal(true);
    try {
      const res = await api.get(`/credits/wallet/${targetType}/${targetId}`);
      setWalletViewData(res.data);
    } catch {
      setWalletViewData({ balance: 0, monthly_allocation: 0, monthly_used: 0, monthly_added: 0, transactions: [] });
    }
  };

  // Open adjust credits modal
  const openAdjustModal = async (targetType, targetId, targetName) => {
    setAdjustTarget(targetName);
    try {
      const res = await api.get(`/credits/wallet/${targetType}/${targetId}`);
      setAdjustForm({
        org_id: targetType === 'organization' ? targetId : '',
        user_id: targetType === 'user' ? targetId : '',
        current_balance: res.data.balance || 0,
        new_balance: res.data.balance || 0,
        reason: ''
      });
    } catch {
      setAdjustForm({ org_id: targetType === 'organization' ? targetId : '', user_id: targetType === 'user' ? targetId : '', current_balance: 0, new_balance: 0, reason: '' });
    }
    setShowAdjustModal(true);
  };

  const handleAdjustCredits = async () => {
    setSaving(true);
    try {
      const payload = { new_balance: adjustForm.new_balance, reason: adjustForm.reason };
      if (adjustForm.org_id) payload.organization_id = adjustForm.org_id;
      else payload.user_id = adjustForm.user_id;
      await api.post('/credits/adjust', payload);
      toast.success('Credits adjusted');
      setShowAdjustModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust');
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    setSaving(true);
    try {
      if (deleteTarget.type === 'User') {
        await api.delete(`/users/${deleteTarget.id}`);
      } else {
        await api.delete(`/organizations/${deleteTarget.id}`);
      }
      toast.success(`${deleteTarget.type} deleted`);
      setShowDeleteModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // Edit org handler
  const handleEditOrg = async () => {
    setSaving(true);
    try {
      await api.patch(`/organizations/${editOrgForm.id}`, {
        name: editOrgForm.name,
        poc_name: editOrgForm.poc_name,
        poc_email: editOrgForm.poc_email,
        poc_phone: editOrgForm.poc_phone,
        gst_number: editOrgForm.gst_number
      });
      toast.success('Organization updated');
      setShowEditOrgModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Edit user handler
  const handleEditUser = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${editUserForm.id}`, {
        name: editUserForm.name,
        email: editUserForm.email,
        phone: editUserForm.phone,
        organization_id: editUserForm.organization_id || null,
        is_active: editUserForm.is_active
      });
      toast.success('User updated');
      setShowEditUserModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Add bookable handler
  const handleAddBookable = async () => {
    if (!bookableForm.name || !bookableForm.venue_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const amenitiesArray = Object.entries(bookableForm.amenities)
        .filter(([_, selected]) => selected)
        .map(([key, _]) => key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
      
      await api.post('/bookables', {
        ...bookableForm,
        amenities: amenitiesArray,
        half_day_rate: Math.round(bookableForm.hourly_rate * 4 * 0.9),
        full_day_rate: Math.round(bookableForm.hourly_rate * 8 * 0.75)
      });
      
      toast.success('Bookable created successfully');
      setShowBookableModal(false);
      setBookableForm({
        name: '',
        venue_id: '',
        bookable_type: 'meeting_room',
        capacity: 4,
        hourly_rate: 500,
        description: '',
        amenities: {
          wifi: true,
          whiteboard: true,
          tv_screen: true,
          ac: true,
          projector: false,
          video_conferencing: false,
          coffee_tea: false,
          water: true,
          power_outlets: true,
          natural_light: false
        }
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bookable');
    } finally {
      setSaving(false);
    }
  };

  // Cancel booking handler
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    try {
      await api.post(`/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  // Open edit booking modal
  const openEditBooking = (booking) => {
    setEditBookingForm({
      id: booking.id,
      booking_date: booking.booking_date,
      start_time: booking.start_time,
      end_time: booking.end_time,
      venue_id: booking.venue_id,
      bookable_id: booking.bookable_id,
      attendees: booking.attendees || 1,
      total_amount: booking.total_amount || 0,
      discount_amount: booking.discount_amount || 0,
      notes: booking.notes || '',
      status: booking.status,
    });
    setShowEditBookingModal(true);
  };

  // Save edited booking
  const handleEditBooking = async () => {
    setSaving(true);
    try {
      const { id, ...updates } = editBookingForm;
      await api.patch(`/bookings/${id}`, updates);
      toast.success('Booking updated');
      setShowEditBookingModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };


  // Invoice handlers
  const handleViewInvoice = async (bookingId) => {
    try {
      toast.info('Generating invoice...');
      const res = await api.get(`/bookings/${bookingId}/invoice`);
      if (res.data.has_invoice && res.data.invoice_url) {
        window.open(res.data.invoice_url, '_blank');
      } else if (res.data.has_invoice) {
        toast.success(`Invoice #${res.data.zoho_invoice_number || ''} generated`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleDownloadInvoice = async (bookingId) => {
    try {
      toast.info('Downloading PDF...');
      const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Clayworks_Invoice_${bookingId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // If PDF download fails, try generating the invoice first
      try {
        await api.get(`/bookings/${bookingId}/invoice`);
        const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Clayworks_Invoice_${bookingId.slice(0, 8)}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to download invoice');
      }
    }
  };


  // Filter bookings by venue and date
  const getBookingDateRange = () => {
    const now = new Date();
    switch (bookingDateFilter) {
      case 'today': return { start: startOfDay(now), end: endOfDay(now) };
      case 'week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month': return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'custom': return { start: bookingCustomStart ? startOfDay(bookingCustomStart) : null, end: bookingCustomEnd ? endOfDay(bookingCustomEnd) : null };
      default: return null;
    }
  };

  const filteredBookings = useMemo(() => {
    let result = selectedVenue === 'all' ? bookings : bookings.filter(b => b.venue_id === selectedVenue);
    const range = getBookingDateRange();
    if (range) {
      result = result.filter(b => {
        if (!b.booking_date) return false;
        const bd = parseISO(b.booking_date);
        if (range.start && bd < range.start) return false;
        if (range.end && bd > range.end) return false;
        return true;
      });
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(b => {
        const u = users.find(x => x.id === b.user_id);
        const haystack = [
          b.id, b.guest_name, b.guest_email, b.guest_phone,
          u?.name, u?.email, u?.phone,
          venues.find(v => v.id === b.venue_id)?.name,
          bookables.find(x => x.id === b.bookable_id)?.name,
        ];
        return haystack.some(h => h && String(h).toLowerCase().includes(q));
      });
    }
    // Sort by booking_date descending (not creation order) so the most
    // recent bookings surface first while a date-range filter still shows
    // the full chronological span within that range.
    result = [...result].sort((a, b) => (b.booking_date || '').localeCompare(a.booking_date || ''));
    return result;
  }, [bookings, users, venues, bookables, searchQuery, selectedVenue, bookingDateFilter, bookingCustomStart, bookingCustomEnd]);

  const bookingTotalPages = Math.max(1, Math.ceil(filteredBookings.length / BOOKING_PAGE_SIZE));
  const bookingCurrentPage = Math.min(bookingPage, bookingTotalPages);
  const pagedBookings = filteredBookings.slice(
    (bookingCurrentPage - 1) * BOOKING_PAGE_SIZE,
    bookingCurrentPage * BOOKING_PAGE_SIZE
  );

  useEffect(() => {
    setBookingPage(1);
  }, [selectedVenue, searchQuery, bookingDateFilter, bookingCustomStart, bookingCustomEnd]);

  const filteredTodayBookings = selectedVenue === 'all'
    ? todayBookings
    : todayBookings.filter(b => b.venue_id === selectedVenue);

  // Credits tab search: orgs by name/email/phone; users by name/email/phone/organization.
  const creditQ = creditSearchQuery.trim().toLowerCase();
  const matchesCreditQ = (...fields) => !creditQ || fields.some(f => f && String(f).toLowerCase().includes(creditQ));
  const creditOrgs = organizations.filter(o => matchesCreditQ(o.name, o.email, o.phone, o.contact_email, o.contact_phone));
  const creditUsers = users
    .filter(u => u.role === 'client')
    .filter(u => matchesCreditQ(u.name, u.email, u.phone, organizations.find(o => o.id === u.organization_id)?.name));

  // Get venue/bookable names
  const getVenueName = (venueId) => venues.find(v => v.id === venueId)?.name || 'Unknown';
  const getBookableName = (bookableId) => bookables.find(b => b.id === bookableId)?.name || 'Unknown';
  const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Walk-in';
  const getUserEmail = (userId) => users.find(u => u.id === userId)?.email || '';
  const getUserPhone = (userId) => users.find(u => u.id === userId)?.phone || '';

  const getBookingType = (booking) => {
    if (booking.notes?.startsWith('Walk-in:') || (booking.guest_name && !booking.is_internal_client && booking.created_by_role !== 'sales')) return 'Walk-in';
    if (booking.is_internal_client) return 'Booked by Client';
    if (booking.created_by_role === 'sales') return 'Sales Booking';
    if (booking.is_internal || booking.notes?.startsWith('Internal:')) return 'Internal';
    return 'Booking';
  };

  const getBookingTypeBadgeColor = (type) => {
    switch (type) {
      case 'Walk-in': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'Booked by Client': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'Sales Booking': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'Internal': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getGuestDisplay = (booking) => {
    if (booking.guest_name) {
      return { name: booking.guest_name, email: booking.guest_email || '', phone: booking.guest_phone || '' };
    }
    // registered user → organization (org-only "Book for Client" bookings have
    // no user_id; the API resolves user_name / organization_name for us)
    const bookedUser = booking.user_id && users.find(u => u.id === booking.user_id);
    const name = booking.user_name || bookedUser?.name || booking.organization_name;
    if (name) {
      return { name, email: booking.user_email || bookedUser?.email || '', phone: bookedUser?.phone || '' };
    }
    return { name: 'N/A', email: '', phone: '' };
  };

  // Sidebar items
  const sidebarItems = [
    { id: 'today', label: "Today's Schedule", icon: CalendarDays },
    { id: 'availability', label: 'Availability', icon: Eye },
    { id: 'bookings', label: 'All Bookings', icon: Calendar },
    { id: 'venues', label: 'Venues & Spaces', icon: Building2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'organizations', label: 'Organizations', icon: Briefcase },
    { id: 'credits', label: 'Credits', icon: Coins },
    { id: 'tickets', label: 'Tickets', icon: FileText },
    { id: 'contracts', label: 'Contracts', icon: FileText },
    { id: 'feedback', label: 'Feedback', icon: Star },
    { id: 'approvals', label: 'Approvals', icon: FileText },
    { id: 'virtual_office', label: 'Virtual Office', icon: Briefcase },
    { id: 'ecosystem_inq', label: 'Ecosystem Inquiries', icon: Sparkles },
    { id: 'inventory', label: 'Inventory', icon: Building2 },
    { id: 'credits_overview', label: 'Credits Overview', icon: BarChart3 },
    { id: 'day_passes_overview', label: 'Bulk Day Passes', icon: Ticket },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'vms', label: 'Visitor Log', icon: UserCheck },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`w-56 ${cardBg} border-r ${cardBorder} flex flex-col fixed inset-y-0 z-50`}>
        <div className="p-4 border-b border-border">
          <div className="flex flex-col gap-1.5">
            <img src="/logo.webp" alt="Clayworks" className="h-7 w-auto" />
            <p className="text-xs font-medium text-muted-foreground capitalize tracking-wide">{user?.role?.replace('_', ' ')}</p>
          </div>
        </div>
        
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                activeTab === item.id 
                  ? 'bg-primary text-primary-foreground' 
                  : 'hover:bg-muted text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
        
        <div className="px-3 py-2 border-t border-border space-y-1">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {isDark ? 'Light Mode' : 'Dark Mode'}
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowProfileModal(true)}
              className={`flex items-center gap-2 flex-1 min-w-0 p-2 rounded-xl ${innerCardBg} hover:ring-1 hover:ring-primary/30 transition-all cursor-pointer text-left`}
              data-testid="sidebar-profile-btn"
            >
              {user?.photo_url ? (
                <img src={user.photo_url.startsWith('/') ? `${BACKEND_URL}${user.photo_url}` : user.photo_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Users className="w-3.5 h-3.5 text-primary" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{user?.name}</p>
                <p className="text-[10px] text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive shrink-0 px-2"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <ProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        api={api}
        onProfileUpdate={() => window.location.reload()}
      />

      {/* Main Content */}
      <main className="flex-1 ml-56 overflow-auto">
        {/* Header */}
        <header className={`sticky top-0 z-10 ${cardBg} border-b ${cardBorder} px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold font-heading">
                {sidebarItems.find(i => i.id === activeTab)?.label || 'Dashboard'}
              </h1>
              <p className="text-sm text-muted-foreground">
                Welcome back, {user?.name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <ApprovalsBell api={api} user={user} onClick={() => setActiveTab('approvals')} />
              <Button variant="outline" size="sm" onClick={fetchAllData} className={cardBorder}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              {activeTab === 'today' && (
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => setShowInternalModal(true)} data-testid="btn-book-clayworks" className="bg-[#D5A97A] hover:bg-[#D5A97A]/90 text-white border-none">
                    <Building2 className="w-4 h-4 mr-1" /> For ClayWorks
                  </Button>
                  {(user?.role === 'center_manager' || user?.role === 'cluster_manager') && (
                    <Button size="sm" onClick={() => setShowVisitorModal(true)} data-testid="btn-log-visitor" className="bg-primary hover:bg-primary/90 text-primary-foreground border-none">
                      <ClipboardList className="w-4 h-4 mr-1" /> Log a Visitor
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setShowInternalClientModal(true)} data-testid="btn-book-internal-client" className="bg-[#B97134] hover:bg-[#B97134]/90 text-white border-none">
                    <Users className="w-4 h-4 mr-1" /> For Internal Clients
                  </Button>
                  <Button size="sm" onClick={() => setShowWalkInModal(true)} data-testid="btn-guest-walkin" className="bg-[#86542A] hover:bg-[#86542A]/90 text-white border-none">
                    <Plus className="w-4 h-4 mr-1" /> Guest / Walk-in
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowSellCreditsModal(true)} data-testid="btn-sell-credits" className={cardBorder}>
                    <Coins className="w-4 h-4 mr-1" /> Sell Credits
                  </Button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {/* Venue Filter */}
          {['today', 'bookings', 'analytics'].includes(activeTab) && (
            <div className="mb-6">
              <Select value={selectedVenue} onValueChange={setSelectedVenue}>
                <SelectTrigger className={`w-64 ${inputBg}`}>
                  <SelectValue placeholder="Filter by Venue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {venues.filter(v => v.is_active).map(venue => (
                    <SelectItem key={venue.id} value={venue.id}>{venue.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Today's Schedule Tab */}
          {activeTab === 'today' && (
            <div className="space-y-6">
              {/* Lock-in expiry alerts (60/30/15/7 days) for this CM's centres */}
              <AlertsBanner api={api} isAdmin={false} onViewAll={() => { setInventorySubTabHint('available_from'); setActiveTab('inventory'); }} />

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-4 text-center">
                    <Calendar className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-bold">{filteredTodayBookings.length}</p>
                    <p className="text-xs text-muted-foreground">Today's Bookings</p>
                  </CardContent>
                </Card>
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-4 text-center">
                    <UserCheck className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">
                      {filteredTodayBookings.filter(b => b.checked_in && !b.checked_out).length}
                    </p>
                    <p className="text-xs text-muted-foreground">Currently In</p>
                  </CardContent>
                </Card>
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">
                      {filteredTodayBookings.filter(b => b.status === 'pending').length}
                    </p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </CardContent>
                </Card>
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-bold">
                      {filteredTodayBookings.filter(b => b.checked_out).length}
                    </p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </CardContent>
                </Card>
              </div>

              {/* Today's Bookings List */}
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading">Today's Schedule - {new Date().toLocaleDateString()}</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => setShowBlockModal(true)} className={cardBorder}>
                    <Ban className="w-4 h-4 mr-2" /> Block Slot
                  </Button>
                </CardHeader>
                <CardContent>
                  {filteredTodayBookings.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No bookings scheduled for today</p>
                      <Button className="mt-4" onClick={() => setShowWalkInModal(true)}>
                        <Plus className="w-4 h-4 mr-2" /> Create Walk-in Booking
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredTodayBookings
                        .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                        .map(booking => (
                        <div 
                          key={booking.id} 
                          className={`p-4 rounded-xl border ${cardBorder} ${innerCardBg} ${
                            booking.checked_in && !booking.checked_out ? 'ring-2 ring-green-500/50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                booking.checked_out ? 'bg-blue-500/20 text-blue-500' :
                                booking.checked_in ? 'bg-green-500/20 text-green-500' :
                                'bg-primary/20 text-primary'
                              }`}>
                                <Clock className="w-6 h-6" />
                              </div>
                              <div>
                                <p className="font-medium">{booking.start_time} - {booking.end_time}</p>
                                <p className="text-sm text-muted-foreground">
                                  {getBookableName(booking.bookable_id)} • {getVenueName(booking.venue_id)}
                                </p>
                                <p className="text-sm">
                                  <span className="text-muted-foreground">
                                    {booking.notes?.startsWith('Walk-in:') ? 'Walk-in:' : booking.guest_email ? 'Guest:' : 'Booked by:'}
                                  </span>{' '}
                                  {booking.notes?.startsWith('Walk-in:')
                                    ? booking.notes.split('\n')[0].replace('Walk-in: ', '').split(' | ')[0]
                                    : booking.guest_email || getGuestDisplay(booking).name}
                                  {booking.attendees > 1 && ` (+${booking.attendees - 1} others)`}
                                </p>
                                {booking.discount_amount > 0 && (
                                  <p className="text-xs text-green-600">Discount: ₹{booking.discount_amount}</p>
                                )}
                                {getAddOnChips(booking).length > 0 && (
                                  <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`today-addons-${booking.id}`}>
                                    <Package className="w-3 h-3 text-amber-600" />
                                    {getAddOnChips(booking).map(a => (
                                      <span key={a.key} className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/30">
                                        {a.label}{a.qty > 1 ? ` x${a.qty}` : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={
                                booking.checked_out ? 'default' :
                                booking.checked_in ? 'success' :
                                booking.status === 'confirmed' ? 'secondary' : 'outline'
                              }>
                                {booking.checked_out ? 'Completed' :
                                 booking.checked_in ? 'In Progress' :
                                 booking.status}
                              </Badge>
                              
                              {!booking.checked_in && booking.status !== 'cancelled' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleCheckIn(booking.id)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <UserCheck className="w-4 h-4 mr-1" /> Check In
                                </Button>
                              )}
                              
                              {booking.checked_in && !booking.checked_out && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleCheckOut(booking.id)}
                                  className={cardBorder}
                                >
                                  <UserX className="w-4 h-4 mr-1" /> Check Out
                                </Button>
                              )}
                              
                              {!booking.checked_in && booking.status !== 'cancelled' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCancelBooking(booking.id)}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Availability Tab */}
          {activeTab === 'availability' && (
            <AvailabilityView venues={venues} />
          )}

          {/* All Bookings Tab */}
          {activeTab === 'bookings' && (
            <Card className={`${cardBg} ${cardBorder}`}>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="font-heading">All Bookings</CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await api.get('/bookings/export/csv', { responseType: 'blob' });
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `bookings_export_${new Date().toISOString().slice(0, 10)}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Bookings CSV downloaded');
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Export failed');
                      }
                    }}
                    data-testid="cm-bookings-export-csv"
                  >
                    <Download className="w-4 h-4 mr-2" /> Export CSV (my centres)
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by client name, email, phone, booking ID, venue or space..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-8"
                    data-testid="cm-bookings-search-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      <span className="sr-only">Clear</span>
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  )}
                </div>

                {/* Date Period Filter */}
                <div className="flex flex-wrap gap-2 mb-3" data-testid="cm-date-period-filter">
                  {[
                    { value: 'all', label: 'All Time' },
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'This Week' },
                    { value: 'month', label: 'This Month' },
                    { value: 'custom', label: 'Custom Range' },
                  ].map(p => (
                    <Button
                      key={p.value}
                      size="sm"
                      variant={bookingDateFilter === p.value ? 'default' : 'outline'}
                      onClick={() => { setBookingDateFilter(p.value); if (p.value !== 'custom') { setBookingCustomStart(null); setBookingCustomEnd(null); } }}
                      data-testid={`cm-date-filter-${p.value}`}
                    >
                      {p.value === 'custom' && <CalendarDays className="w-3.5 h-3.5 mr-1.5" />}
                      {p.label}
                    </Button>
                  ))}
                </div>

                {/* Custom Date Range Pickers */}
                {bookingDateFilter === 'custom' && (
                  <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-lg border border-dashed" data-testid="cm-custom-date-range">
                    <Popover open={showBkStartCal} onOpenChange={setShowBkStartCal}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="min-w-[140px] justify-start" data-testid="cm-custom-start-date">
                          <CalendarDays className="w-3.5 h-3.5 mr-2" />
                          {bookingCustomStart ? fmtDate(bookingCustomStart, 'MMM dd, yyyy') : 'Start Date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarWidget mode="single" selected={bookingCustomStart} onSelect={(d) => { setBookingCustomStart(d); setShowBkStartCal(false); }} />
                      </PopoverContent>
                    </Popover>
                    <span className="text-muted-foreground text-sm">to</span>
                    <Popover open={showBkEndCal} onOpenChange={setShowBkEndCal}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="min-w-[140px] justify-start" data-testid="cm-custom-end-date">
                          <CalendarDays className="w-3.5 h-3.5 mr-2" />
                          {bookingCustomEnd ? fmtDate(bookingCustomEnd, 'MMM dd, yyyy') : 'End Date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarWidget mode="single" selected={bookingCustomEnd} onSelect={(d) => { setBookingCustomEnd(d); setShowBkEndCal(false); }} />
                      </PopoverContent>
                    </Popover>
                    {(bookingCustomStart || bookingCustomEnd) && (
                      <Button variant="ghost" size="sm" onClick={() => { setBookingCustomStart(null); setBookingCustomEnd(null); }} className="text-muted-foreground">
                        <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Clear
                      </Button>
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground mb-3">{filteredBookings.length} of {bookings.length} bookings</p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-sm font-medium">ID</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Date</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Time</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Venue</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Space</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Guest</th>
                        <th className="text-left py-3 px-2 text-sm font-medium">Type</th>
                        <th className="text-right py-3 px-2 text-sm font-medium">Amount</th>
                        <th className="text-center py-3 px-2 text-sm font-medium">Payment</th>
                        <th className="text-center py-3 px-2 text-sm font-medium">Status</th>
                        <th className="text-center py-3 px-2 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedBookings.map(booking => {
                        const guest = getGuestDisplay(booking);
                        const bookingType = getBookingType(booking);
                        return (
                        <tr key={booking.id} className="border-b border-border/50">
                          <td className="py-3 px-2 text-sm font-mono">#{booking.id.slice(0, 8)}</td>
                          <td className="py-3 px-2 text-sm">{booking.booking_date}</td>
                          <td className="py-3 px-2 text-sm">{booking.start_time} - {booking.end_time}</td>
                          <td className="py-3 px-2 text-sm">{getVenueName(booking.venue_id)}</td>
                          <td className="py-3 px-2 text-sm">
                            {getBookableName(booking.bookable_id)}
                            {getAddOnChips(booking).length > 0 && (
                              <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`table-addons-${booking.id.slice(0, 8)}`}>
                                {getAddOnChips(booking).map(a => (
                                  <span key={a.key} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/30">
                                    <Package className="w-2.5 h-2.5" />{a.label}{a.qty > 1 ? ` x${a.qty}` : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="text-sm font-medium" data-testid={`booking-guest-name-${booking.id.slice(0, 8)}`}>{guest.name}</div>
                            {guest.email && <div className="text-xs text-muted-foreground">{guest.email}</div>}
                          </td>
                          <td className="py-3 px-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getBookingTypeBadgeColor(bookingType)}`} data-testid={`booking-type-${booking.id.slice(0, 8)}`}>
                              {bookingType}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-sm text-right">₹{booking.total_amount?.toLocaleString()}</td>
                          <td className="py-3 px-2 text-center">
                            {(() => {
                              const pm = booking.is_internal
                                ? { label: 'Internal', cls: 'bg-slate-500/15 text-slate-600 border-slate-500/30' }
                                : (booking.credits_used || 0) > 0 && (booking.total_amount || 0) === 0
                                ? { label: 'Credits', cls: 'bg-amber-500/15 text-amber-700 border-amber-500/30' }
                                : booking.payment_type === 'razorpay_link' || booking.razorpay_payment_link_id || booking.payment_type === 'razorpay' || booking.razorpay_payment_id
                                ? { label: 'Razorpay', cls: 'bg-blue-500/15 text-blue-700 border-blue-500/30' }
                                : booking.payment_type === 'cash'
                                ? { label: 'Cash', cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' }
                                : booking.payment_type === 'manual' || booking.payment_type === 'bank_transfer'
                                ? { label: booking.payment_type === 'bank_transfer' ? 'Bank' : 'Manual', cls: 'bg-purple-500/15 text-purple-700 border-purple-500/30' }
                                : (booking.total_amount || 0) === 0
                                ? { label: 'Free', cls: 'bg-gray-400/15 text-gray-600 border-gray-400/30' }
                                : { label: '—', cls: 'bg-gray-400/15 text-gray-500 border-gray-400/30' };
                              return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${pm.cls}`} data-testid={`cm-booking-payment-${booking.id.slice(0, 8)}`}>{pm.label}</span>;
                            })()}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Badge variant={
                              booking.status === 'confirmed' ? 'success' :
                              booking.status === 'cancelled' ? 'destructive' : 'secondary'
                            }>
                              {booking.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-center">
                            {booking.status !== 'cancelled' && (
                              <div className="flex items-center justify-center gap-1">
                                {['pending', 'pending_payment', 'confirmed'].includes(booking.status) && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setRescheduleBooking(booking)}
                                    className="text-blue-500 hover:text-blue-700"
                                    title="Reschedule (no charge, client is notified)"
                                    data-testid={`cm-reschedule-${booking.id.slice(0, 8)}`}
                                  >
                                    <CalendarClock className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {booking.status === 'pending_payment' && booking.razorpay_payment_link_url && (
                                  <>
                                    <a
                                      href={booking.razorpay_payment_link_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:text-primary/80 inline-flex items-center justify-center w-6 h-6"
                                      title="Open Payment Link"
                                      data-testid={`open-paylink-${booking.id.slice(0, 8)}`}
                                    >
                                      <Link2 className="w-3 h-3" />
                                    </a>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        navigator.clipboard.writeText(booking.razorpay_payment_link_url);
                                        toast.success('Payment link copied');
                                      }}
                                      className="text-slate-600 hover:text-slate-800"
                                      title="Copy Payment Link"
                                      data-testid={`copy-paylink-${booking.id.slice(0, 8)}`}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => shareViaWhatsApp(
                                        `Hi ${guest.name || ''}, here's your Clayworks payment link for ₹${booking.total_amount?.toLocaleString('en-IN')}: ${booking.razorpay_payment_link_url}`,
                                        guest.phone
                                      )}
                                      className="text-green-600 hover:text-green-700 hover:bg-green-500/10"
                                      title="Share via WhatsApp"
                                      data-testid={`whatsapp-paylink-${booking.id.slice(0, 8)}`}
                                    >
                                      <WhatsAppIcon className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                                {(booking.total_amount || 0) > 0 && (booking.credits_used || 0) === 0 && !booking.is_internal && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDownloadInvoice(booking.id)}
                                    className="text-primary hover:text-primary/80"
                                    title="Download Invoice"
                                    data-testid={`invoice-booking-${booking.id.slice(0, 8)}`}
                                  >
                                    <Download className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditBooking(booking)}
                                  className="text-blue-500 hover:text-blue-600"
                                  data-testid={`edit-booking-${booking.id.slice(0, 8)}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleCancelBooking(booking.id)}
                                  className="text-red-500 hover:text-red-600"
                                  data-testid={`cancel-booking-${booking.id.slice(0, 8)}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {bookingTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4" data-testid="cm-bookings-pagination">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBookingPage(p => Math.max(1, p - 1))}
                      disabled={bookingCurrentPage <= 1}
                      data-testid="cm-bookings-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                    </Button>
                    <span className="text-xs text-muted-foreground">Page {bookingCurrentPage} of {bookingTotalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBookingPage(p => Math.min(bookingTotalPages, p + 1))}
                      disabled={bookingCurrentPage >= bookingTotalPages}
                      data-testid="cm-bookings-next-page"
                    >
                      Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Venues & Spaces Tab */}
          {activeTab === 'venues' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setBookableFormVenueId(null); setEditingBookable(null); setShowBookableModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Bookable Space
                </Button>
              </div>
              
              {venues.map(venue => {
                const venueBookables = bookables.filter(b => b.venue_id === venue.id);
                const externalEnabled = venueBookables.filter(b => b.is_active).length;
                const internalEnabled = venueBookables.filter(b => b.is_active_internal !== false).length;
                const fullyDisabled = venueBookables.filter(b => !b.is_active && b.is_active_internal === false).length;

                return (
                <Card key={venue.id} className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted">
                        <img 
                          src={venue.images?.[0]?.startsWith('/') ? `${BACKEND_URL}${venue.images[0]}` : venue.images?.[0] || 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?w=200'}
                          alt={venue.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="font-heading">{venue.name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{venue.address}, {venue.city}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge>{venue.region}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {externalEnabled} public / {internalEnabled} internal / {fullyDisabled} disabled
                          </span>
                          <Badge variant="outline">{(venue.floors || []).length} Floor{(venue.floors || []).length === 1 ? '' : 's'} configured</Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => setParkingVenue(venue)} data-testid={`cm-venue-parking-config-${venue.id}`}>
                          <Layers className="w-4 h-4 mr-1.5" /> Parking Config
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setFloorsVenue(venue)} data-testid={`cm-venue-floors-config-${venue.id}`}>
                          <Building className="w-4 h-4 mr-1.5" /> Floors Config
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {venueBookables.map(bookable => {
                        const extActive = bookable.is_active;
                        const intActive = bookable.is_active_internal !== false;  // default true for legacy
                        let statusLabel, statusColor;
                        if (extActive && intActive) { statusLabel = 'Public + Internal'; statusColor = 'text-green-500'; }
                        else if (!extActive && intActive) { statusLabel = 'Internal only'; statusColor = 'text-amber-500'; }
                        else if (extActive && !intActive) { statusLabel = 'Public only'; statusColor = 'text-blue-500'; }
                        else { statusLabel = 'Disabled'; statusColor = 'text-red-500'; }
                        const cardFaded = !extActive && !intActive;
                        return (
                        <div
                          key={bookable.id}
                          className={`p-4 rounded-xl border ${cardBorder} ${
                            cardFaded ? 'bg-muted/30 opacity-60' : innerCardBg
                          }`}
                          data-testid={`bookable-card-${bookable.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className={`font-medium ${cardFaded ? 'text-muted-foreground' : ''}`}>
                                {bookable.name}
                              </p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {bookable.bookable_type?.replace('_', ' ')}
                              </p>
                            </div>
                            <Badge variant="outline">{bookable.capacity} seats</Badge>
                          </div>
                          <div className="text-sm space-y-1 mb-3">
                            <p>₹{bookable.hourly_rate}/hr</p>
                            <p className="text-muted-foreground text-xs">
                              {bookable.amenities?.slice(0, 3).join(', ')}
                              {bookable.amenities?.length > 3 && ` +${bookable.amenities.length - 3} more`}
                            </p>
                          </div>
                          <div className="pt-2 border-t border-border space-y-2">
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-semibold ${statusColor}`} data-testid={`bookable-status-${bookable.id}`}>
                                {statusLabel}
                              </span>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => { setEditingBookable(bookable); setShowBookableModal(true); }}
                                  data-testid={`edit-bookable-${bookable.id}`}
                                >
                                  <Edit className="w-3 h-3 mr-1" /> Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => {
                                    const link = `${window.location.origin}/preview/space/${bookable.id}?ref=${user?.id || ''}`;
                                    navigator.clipboard.writeText(link);
                                    toast.success('Preview link copied!');
                                  }}
                                  data-testid={`share-bookable-${bookable.id}`}
                                >
                                  <Share2 className="w-3 h-3 mr-1" /> Share
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs text-green-600 hover:text-green-700 hover:bg-green-500/10"
                                  onClick={() => {
                                    const link = `${window.location.origin}/preview/space/${bookable.id}?ref=${user?.id || ''}`;
                                    shareViaWhatsApp(`Check out ${bookable.name} at ${venue.name}: ${link}`);
                                  }}
                                  data-testid={`whatsapp-bookable-${bookable.id}`}
                                  title="Share via WhatsApp"
                                >
                                  <WhatsAppIcon className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex items-center justify-between p-2 rounded-md bg-background/50 border border-border/50">
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium">External</span>
                                  <span className="text-[10px] text-muted-foreground">Clients can book</span>
                                </div>
                                <Switch
                                  checked={extActive}
                                  onCheckedChange={async (checked) => {
                                    try {
                                      await api.patch(`/bookables/${bookable.id}`, { is_active: checked });
                                      toast.success(`${bookable.name} — external ${checked ? 'enabled' : 'disabled'}`);
                                      fetchAllData();
                                    } catch (error) {
                                      toast.error('Failed to update bookable status');
                                    }
                                  }}
                                  data-testid={`toggle-external-${bookable.id}`}
                                />
                              </div>
                              <div className="flex items-center justify-between p-2 rounded-md bg-background/50 border border-border/50">
                                <div className="flex flex-col">
                                  <span className="text-xs font-medium">Internal</span>
                                  <span className="text-[10px] text-muted-foreground">CM/Admin use</span>
                                </div>
                                <Switch
                                  checked={intActive}
                                  onCheckedChange={async (checked) => {
                                    try {
                                      await api.patch(`/bookables/${bookable.id}`, { is_active_internal: checked });
                                      toast.success(`${bookable.name} — internal ${checked ? 'enabled' : 'disabled'}`);
                                      fetchAllData();
                                    } catch (error) {
                                      toast.error('Failed to update bookable status');
                                    }
                                  }}
                                  data-testid={`toggle-internal-${bookable.id}`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                      {venueBookables.length === 0 && (
                        <div className="col-span-full text-center py-8 text-muted-foreground">
                          <p>No bookable spaces in this venue</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-2"
                            onClick={() => {
                              setBookableFormVenueId(venue.id);
                              setEditingBookable(null);
                              setShowBookableModal(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add Space
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <Card className={`${cardBg} ${cardBorder}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Users</CardTitle>
                <Button onClick={() => setShowUserModal(true)}>
                  <UserPlus className="w-4 h-4 mr-2" /> Add User
                </Button>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, phone, or organization..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="pl-9 pr-8"
                      data-testid="cm-users-search-input"
                    />
                    {userSearchQuery && (
                      <button onClick={() => setUserSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <span className="sr-only">Clear</span>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                </div>
                {(() => {
                  const q = userSearchQuery.toLowerCase();
                  const filtered = users.filter(u => u.role === 'client').filter(u =>
                    !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.toLowerCase().includes(q) || organizations.find(o => o.id === u.organization_id)?.name?.toLowerCase().includes(q)
                  );
                  return (
                    <>
                      <p className="text-xs text-muted-foreground mb-3">{filtered.length} users</p>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-3 px-2 text-sm font-medium">Name</th>
                              <th className="text-left py-3 px-2 text-sm font-medium">Email</th>
                              <th className="text-left py-3 px-2 text-sm font-medium">Phone</th>
                              <th className="text-left py-3 px-2 text-sm font-medium">Type</th>
                              <th className="text-left py-3 px-2 text-sm font-medium">Organization</th>
                              <th className="text-center py-3 px-2 text-sm font-medium">Status</th>
                              <th className="text-center py-3 px-2 text-sm font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.length === 0 ? (
                              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{userSearchQuery ? 'No users match your search' : 'No users yet'}</td></tr>
                            ) : filtered.slice(0, 50).map(u => (
                              <tr key={u.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                                <td className="py-3 px-2">{u.name}</td>
                                <td className="py-3 px-2 text-sm">{u.email}</td>
                                <td className="py-3 px-2 text-sm">{u.phone || '-'}</td>
                                <td className="py-3 px-2">
                                  <Badge variant="outline">{u.user_type?.toUpperCase()}</Badge>
                                </td>
                                <td className="py-3 px-2 text-sm">
                                  {organizations.find(o => o.id === u.organization_id)?.name || '-'}
                                </td>
                                <td className="py-3 px-2 text-center">
                                  <Badge variant={u.is_active ? 'success' : 'secondary'}>
                                    {u.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td className="py-3 px-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button size="sm" variant="ghost" data-testid={`cm-edit-user-${u.id}`} onClick={() => { setEditUserForm(u); setShowEditUserModal(true); }}>
                                      <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => { setDeleteTarget({ id: u.id, name: u.name, type: 'User' }); setShowDeleteModal(true); }}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <Card className={`${cardBg} ${cardBorder}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">Organizations</CardTitle>
                <Button onClick={() => setShowOrgModal(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Organization
                </Button>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, or GSTIN..."
                      value={orgSearchQuery}
                      onChange={(e) => setOrgSearchQuery(e.target.value)}
                      className="pl-9 pr-8"
                      data-testid="cm-orgs-search-input"
                    />
                    {orgSearchQuery && (
                      <button onClick={() => setOrgSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <span className="sr-only">Clear</span>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                </div>
                {(() => {
                  const q = orgSearchQuery.toLowerCase();
                  const filtered = organizations.filter(org =>
                    !q || org.name?.toLowerCase().includes(q) || org.billing_email?.toLowerCase().includes(q) || org.gstin?.toLowerCase().includes(q)
                  );
                  return (
                    <>
                      <p className="text-xs text-muted-foreground mb-3">{filtered.length} of {organizations.length} organizations</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        {filtered.length === 0 ? (
                          <p className="text-muted-foreground text-center py-8 col-span-2">{orgSearchQuery ? 'No organizations match your search' : 'No organizations yet'}</p>
                        ) : filtered.map(org => (
                          <div key={org.id} className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-medium">{org.name}</p>
                                <p className="text-xs text-muted-foreground">{org.gstin || 'No GSTIN'}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge variant={org.is_active ? 'success' : 'secondary'}>
                                  {org.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </div>
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Mail className="w-3 h-3" /> {org.billing_email}
                              </div>
                              {org.phone && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Phone className="w-3 h-3" /> {org.phone}
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Users className="w-3 h-3" /> 
                                {users.filter(u => u.organization_id === org.id).length} users
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 flex-wrap">
                              <Button size="sm" variant="outline" data-testid={`cm-edit-org-${org.id}`} onClick={() => { setEditOrgForm(org); setShowEditOrgModal(true); }}>
                                <Pencil className="w-3 h-3 mr-1" /> Edit
                              </Button>
                              <Button size="sm" variant="destructive" className="h-8" onClick={() => { setDeleteTarget({ id: org.id, name: org.name, type: 'Organization' }); setShowDeleteModal(true); }}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Credits Tab */}
          {activeTab === 'credits' && (
            <div className="space-y-6">
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading">Credit Allocation</CardTitle>
                  <Button onClick={() => setShowCreditModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Allocate Credits
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <Card className={`${innerCardBg} ${cardBorder}`}>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">Base Price</p>
                        <p className="text-2xl font-bold">₹100</p>
                        <p className="text-xs text-muted-foreground">per credit</p>
                      </CardContent>
                    </Card>
                    <Card className={`${innerCardBg} ${cardBorder}`}>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">GST Rate</p>
                        <p className="text-2xl font-bold">18%</p>
                        <p className="text-xs text-muted-foreground">included</p>
                      </CardContent>
                    </Card>
                    <Card className={`${innerCardBg} ${cardBorder}`}>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-muted-foreground">Final Price</p>
                        <p className="text-2xl font-bold text-primary">₹118</p>
                        <p className="text-xs text-muted-foreground">per credit</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search organizations or users by name, email, or phone..."
                      value={creditSearchQuery}
                      onChange={(e) => setCreditSearchQuery(e.target.value)}
                      className="pl-9 pr-8"
                      data-testid="cm-credits-search-input"
                    />
                    {creditSearchQuery && (
                      <button onClick={() => setCreditSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <span className="sr-only">Clear</span>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>

                  <h4 className="font-medium mb-3">Organizations</h4>
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {creditOrgs.length === 0 && (
                      <p className="text-muted-foreground text-sm col-span-2">{creditSearchQuery ? 'No organizations match your search' : 'No organizations yet'}</p>
                    )}
                    {creditOrgs.map(org => (
                      <div key={org.id} className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">{org.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {users.filter(u => u.organization_id === org.id).length} users
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => handleViewWallet('organization', org.id, org.name)} data-testid={`view-wallet-org-${org.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setCreditForm({...creditForm, target_type: 'organization', target_id: org.id}); setShowCreditModal(true); }}>
                            <Plus className="w-3 h-3 mr-1" /> Credits
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openAdjustModal('organization', org.id, org.name)}>
                            <Pencil className="w-3 h-3 mr-1" /> Adjust
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditOrgForm(org); setShowEditOrgModal(true); }}>
                            <FileText className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="destructive" className="h-8" onClick={() => { setDeleteTarget({ id: org.id, name: org.name, type: 'Organization' }); setShowDeleteModal(true); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <h4 className="font-medium mb-3">Individual Users</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {creditUsers.length === 0 && (
                      <p className="text-muted-foreground text-sm col-span-2">{creditSearchQuery ? 'No users match your search' : 'No users yet'}</p>
                    )}
                    {creditUsers.map(u => (
                      <div key={u.id} className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => handleViewWallet('user', u.id, u.name)} data-testid={`view-wallet-user-${u.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setCreditForm({...creditForm, target_type: 'user', target_id: u.id}); setShowCreditModal(true); }}>
                            <Plus className="w-3 h-3 mr-1" /> Credits
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openAdjustModal('user', u.id, u.name)}>
                            <Pencil className="w-3 h-3 mr-1" /> Adjust
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditUserForm(u); setShowEditUserModal(true); }}>
                            <FileText className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="destructive" className="h-8" onClick={() => { setDeleteTarget({ id: u.id, name: u.name, type: 'User' }); setShowDeleteModal(true); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="space-y-6" data-testid="cm-tickets-tab">
              {/* Ticket Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Open', count: cmTickets.filter(t => t.status === 'open').length, color: 'text-red-500 bg-red-500/10' },
                  { label: 'In Progress', count: cmTickets.filter(t => t.status === 'in_progress').length, color: 'text-amber-500 bg-amber-500/10' },
                  { label: 'Resolved', count: cmTickets.filter(t => t.status === 'resolved').length, color: 'text-emerald-500 bg-emerald-500/10' },
                  { label: 'Total', count: cmTickets.length, color: 'text-primary bg-primary/10' },
                ].map(s => (
                  <Card key={s.label} className={`${cardBg} ${cardBorder}`}>
                    <CardContent className="p-4 text-center">
                      <p className={`text-2xl font-bold ${s.color.split(' ')[0]}`}>{s.count}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Ticket List */}
              {cmTickets.length === 0 ? (
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
                    <p className="text-muted-foreground">No tickets raised yet</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading">Client Tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {cmTickets.map(ticket => (
                      <div key={ticket.id} className={`p-4 rounded-lg border ${cardBorder} space-y-2`} data-testid={`cm-ticket-${ticket.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{ticket.user_name} <span className="text-muted-foreground font-normal">({ticket.user_email})</span></p>
                            <p className="text-sm mt-1">{ticket.description}</p>
                            {ticket.venue_name && (
                              <p className="text-xs text-muted-foreground mt-1">Venue: {ticket.venue_name}</p>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1">{new Date(ticket.created_at).toLocaleString()}</p>
                          </div>
                          <Select
                            value={ticket.status}
                            onValueChange={async (newStatus) => {
                              try {
                                await api.patch(`/tickets/${ticket.id}`, { status: newStatus });
                                toast.success('Ticket status updated');
                                const res = await api.get('/tickets');
                                setCmTickets(res.data);
                              } catch { toast.error('Failed to update'); }
                            }}
                          >
                            <SelectTrigger className="w-[130px] h-8 text-xs shrink-0" data-testid={`ticket-status-${ticket.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {ticket.photo_url && (
                          <img
                            src={ticket.photo_url.startsWith('/') ? `${BACKEND_URL}${ticket.photo_url}` : ticket.photo_url}
                            alt="Issue"
                            className="w-40 h-40 rounded-lg object-cover border border-border cursor-pointer"
                            onClick={() => window.open(ticket.photo_url.startsWith('/') ? `${BACKEND_URL}${ticket.photo_url}` : ticket.photo_url, '_blank')}
                          />
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Contracts Tab */}
          {activeTab === 'contracts' && (
            <ContractsTab api={api} venues={venues} userRole={user?.role} />
          )}

          {/* Feedback Tab */}
          {activeTab === 'feedback' && (
            <FeedbackTab api={api} venues={venues} isAdminOrHM={false} />
          )}

          {/* Approvals Tab */}
          {activeTab === 'approvals' && (
            <ApprovalsModule api={api} user={user} />
          )}

          {/* Ecosystem Inquiries Tab */}
          {activeTab === 'ecosystem_inq' && (
            <EcosystemInquiriesTab api={api} />
          )}

          {/* Virtual Office Tab */}
          {activeTab === 'virtual_office' && (
            <VirtualOfficeTab api={api} theme={{ cardBg, cardBorder, innerCardBg: cardBg, inputBg }} />
          )}

          {/* Credits Overview Tab */}
          {activeTab === 'credits_overview' && (
            <CreditsOverviewTab api={api} isAdmin={false} />
          )}

          {/* Day Passes Overview Tab */}
          {activeTab === 'day_passes_overview' && (
            <DayPassOverviewTab api={api} isAdmin={false} venues={allVenues} myVenues={venues} />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <RoleAnalytics
              role="cm"
              venueId={selectedVenue && selectedVenue !== 'all' ? selectedVenue : null}
            />
          )}

          {activeTab === 'inventory' && (
            <InventoryTab api={api} user={user} venues={(allVenues.length ? allVenues : venues).filter(v => v.is_active !== false)} showAlerts={false} initialSubTab={inventorySubTabHint} onInitialSubTabUsed={() => setInventorySubTabHint(null)} />
          )}

          {/* Visitor Log (VMS) Tab — shows only visitors this manager has logged */}
          {activeTab === 'vms' && (
            <VMSTab api={api} venues={venues} ownOnly />
          )}
        </div>
      </main>

      {/* Walk-in Booking Modal */}
      <WalkInModal open={showWalkInModal} onClose={setShowWalkInModal} form={walkInForm} setForm={setWalkInForm} onSubmit={handleWalkInBooking} saving={saving} venues={venues} bookables={bookables} theme={{ cardBg, cardBorder, inputBg }} holidayDates={holidayDates} />
      <SellCreditsModal open={showSellCreditsModal} onClose={setShowSellCreditsModal} onLinkSent={setPaymentLinkResult} theme={{ cardBg, cardBorder, inputBg }} />
      <BlockSlotModal open={showBlockModal} onClose={setShowBlockModal} form={blockForm} setForm={setBlockForm} onSubmit={handleBlockTimeSlot} saving={saving} venues={venues} bookables={bookables} theme={{ cardBg, cardBorder, inputBg }} />
      <AddUserModal open={showUserModal} onClose={setShowUserModal} form={userForm} setForm={setUserForm} onSubmit={handleAddUser} saving={saving} organizations={organizations} theme={{ cardBg, cardBorder, inputBg }} />
      <AddOrgModal open={showOrgModal} onClose={setShowOrgModal} form={orgForm} setForm={setOrgForm} onSubmit={handleAddOrg} saving={saving} theme={{ cardBg, cardBorder, inputBg }} />
      <AllocateCreditsModal open={showCreditModal} onClose={setShowCreditModal} form={creditForm} setForm={setCreditForm} onSubmit={handleAllocateCredits} saving={saving} organizations={organizations} users={users} theme={{ cardBg, cardBorder, inputBg }} />
      <BookableFormModal open={showBookableModal} onClose={() => { setShowBookableModal(false); setEditingBookable(null); }} api={api} onSuccess={fetchAllData} venues={venues} editBookable={editingBookable} preselectedVenueId={bookableFormVenueId} />
      <InternalBookingModal open={showInternalModal} onClose={setShowInternalModal} form={internalForm} setForm={setInternalForm} onSubmit={handleInternalBooking} saving={saving} venues={venues} bookables={bookables} theme={{ cardBg, cardBorder, inputBg }} />
      <InternalClientModal open={showInternalClientModal} onClose={setShowInternalClientModal} form={internalClientForm} setForm={setInternalClientForm} onSubmit={handleInternalClientBooking} saving={saving} venues={venues} bookables={bookables} organizations={organizations} users={users} theme={{ cardBg, cardBorder, inputBg }} orgCreditInfo={orgCreditInfo} creditRates={creditRates} holidayDates={holidayDates} user={user} />
      <ParkingConfigModal venue={parkingVenue} onClose={() => setParkingVenue(null)} api={api} onSaved={fetchAllData} />
      <FloorsConfigModal venue={floorsVenue} onClose={() => setFloorsVenue(null)} api={api} onSaved={fetchAllData} />
      <LogVisitorModal open={showVisitorModal} onClose={setShowVisitorModal} form={visitorForm} setForm={setVisitorForm} onSubmit={handleLogVisitor} saving={saving} venues={allVenues} theme={{ cardBg, cardBorder, inputBg }} />
      <WalletViewerModal open={showWalletModal} onClose={setShowWalletModal} walletData={walletViewData} targetName={walletViewTarget} theme={{ cardBg, cardBorder, inputBg }} />
      <AdjustCreditsModal open={showAdjustModal} onClose={setShowAdjustModal} form={adjustForm} setForm={setAdjustForm} onSubmit={handleAdjustCredits} saving={saving} targetName={adjustTarget} theme={{ cardBg, cardBorder, inputBg }} />
      <DeleteConfirmModal open={showDeleteModal} onClose={setShowDeleteModal} onConfirm={handleDelete} saving={saving} itemName={deleteTarget.name} itemType={deleteTarget.type} theme={{ cardBg, cardBorder, inputBg }} />
      <EditOrgModal open={showEditOrgModal} onClose={setShowEditOrgModal} form={editOrgForm} setForm={setEditOrgForm} onSubmit={handleEditOrg} saving={saving} theme={{ cardBg, cardBorder, inputBg }} />
      <EditUserModal open={showEditUserModal} onClose={setShowEditUserModal} form={editUserForm} setForm={setEditUserForm} onSubmit={handleEditUser} saving={saving} organizations={organizations} theme={{ cardBg, cardBorder, inputBg }} />

      {/* Payment Link Success Dialog */}
      <Dialog open={!!paymentLinkResult} onOpenChange={() => setPaymentLinkResult(null)}>
        <DialogContent className={`${cardBg} ${cardBorder} max-w-md`}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Payment Link Sent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{paymentLinkResult?.message}</p>
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-muted-foreground mb-1">Guest</p>
              <p className="font-medium text-sm">{paymentLinkResult?.guest_name} ({paymentLinkResult?.guest_email})</p>
            </div>
            <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
              <p className="text-sm text-muted-foreground mb-1">Amount</p>
              <p className="font-bold text-lg">₹{paymentLinkResult?.total_amount?.toLocaleString('en-IN')}</p>
            </div>
            {paymentLinkResult?.sale_offer?.rewards?.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40" data-testid="cm-payment-link-offer">
                <p className="text-sm text-muted-foreground mb-1">🪔 Sale offer, added to the customer's account once paid</p>
                {paymentLinkResult.sale_offer.rewards.map((r, i) => (
                  <p key={r.booking_id || i} className="text-sm font-medium">{r.summary}{r.bookable_name ? ` · ${r.bookable_name}` : ''}</p>
                ))}
              </div>
            )}
            {paymentLinkResult?.slots?.length > 1 && (
              <div className="space-y-1.5" data-testid="cm-payment-link-slots">
                <p className="text-sm font-medium">{paymentLinkResult.slots.length} Slots Booked</p>
                {paymentLinkResult.slots.map((s) => (
                  <div key={s.booking_id} className={`flex items-center justify-between text-xs p-2 rounded-lg ${inputBg}`}>
                    <span>{s.bookable_name} · {s.venue_name} · {s.booking_date}, {s.start_time}-{s.end_time}</span>
                    <span className="font-medium whitespace-nowrap ml-2">₹{s.total_amount?.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
            {paymentLinkResult?.payment_link && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Payment Link</p>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={paymentLinkResult.payment_link}
                    className={`${inputBg} text-xs`}
                    data-testid="cm-payment-link-url"
                  />
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(paymentLinkResult.payment_link);
                    toast.success('Payment link copied');
                  }} data-testid="cm-copy-payment-link-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-600 hover:text-green-700 hover:bg-green-500/10"
                    onClick={() => shareViaWhatsApp(`Hi ${paymentLinkResult?.guest_name || ''}, here's your Clayworks payment link for ₹${paymentLinkResult?.total_amount?.toLocaleString('en-IN')}: ${paymentLinkResult.payment_link}`, paymentLinkResult?.guest_phone)}
                    data-testid="cm-whatsapp-payment-link-btn"
                    title="Share via WhatsApp"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                  </Button>
                  <a href={paymentLinkResult.payment_link} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" data-testid="cm-open-payment-link-btn">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
                <p className="text-xs text-muted-foreground">Share this link with the customer via WhatsApp or other channels</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setPaymentLinkResult(null)} data-testid="cm-payment-link-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={showEditBookingModal} onOpenChange={setShowEditBookingModal}>
        <DialogContent className={`${cardBg} ${cardBorder} max-w-lg max-h-[85vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" /> Edit Booking
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Venue</Label>
                <Select value={editBookingForm.venue_id || ''} onValueChange={(v) => setEditBookingForm(f => ({...f, venue_id: v, bookable_id: ''}))}>
                  <SelectTrigger className={inputBg} data-testid="edit-venue-select"><SelectValue placeholder="Select Venue" /></SelectTrigger>
                  <SelectContent>
                    {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Space</Label>
                <Select value={editBookingForm.bookable_id || ''} onValueChange={(v) => setEditBookingForm(f => ({...f, bookable_id: v}))}>
                  <SelectTrigger className={inputBg} data-testid="edit-bookable-select"><SelectValue placeholder="Select Space" /></SelectTrigger>
                  <SelectContent>
                    {bookables.filter(b => b.venue_id === editBookingForm.venue_id).map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={editBookingForm.booking_date || ''} onChange={(e) => setEditBookingForm(f => ({...f, booking_date: e.target.value}))} className={inputBg} data-testid="edit-date-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Select value={editBookingForm.start_time || ''} onValueChange={(v) => setEditBookingForm(f => ({...f, start_time: v, end_time: ''}))}>
                  <SelectTrigger className={inputBg} data-testid="edit-start-time"><SelectValue placeholder="Start" /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Select value={editBookingForm.end_time || ''} onValueChange={(v) => setEditBookingForm(f => ({...f, end_time: v}))}>
                  <SelectTrigger className={inputBg} data-testid="edit-end-time"><SelectValue placeholder="End" /></SelectTrigger>
                  <SelectContent>
                    {getEndSlots(editBookingForm.start_time).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input type="number" min={0} value={editBookingForm.total_amount || ''} onChange={(e) => setEditBookingForm(f => ({...f, total_amount: parseFloat(e.target.value) || 0}))} className={inputBg} data-testid="edit-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>Discount (₹)</Label>
                <Input type="number" min={0} value={editBookingForm.discount_amount || ''} onChange={(e) => setEditBookingForm(f => ({...f, discount_amount: parseFloat(e.target.value) || 0}))} className={inputBg} data-testid="edit-discount-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editBookingForm.status || ''} onValueChange={(v) => setEditBookingForm(f => ({...f, status: v}))}>
                <SelectTrigger className={inputBg} data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="pending_payment">Pending Payment</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={editBookingForm.notes || ''} onChange={(e) => setEditBookingForm(f => ({...f, notes: e.target.value}))} className={inputBg} data-testid="edit-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditBookingModal(false)} className={cardBorder}>Cancel</Button>
            <Button onClick={handleEditBooking} disabled={saving || !editBookingForm.bookable_id} data-testid="edit-booking-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RescheduleBookingModal
        open={!!rescheduleBooking}
        onClose={() => setRescheduleBooking(null)}
        booking={rescheduleBooking}
        api={api}
        venues={venues}
        onDone={() => window.location.reload()}
      />
    </div>
  );
};

export default CenterManagerDashboard;
